// src/components/reports/precon-report-builder/precon-handoff.ts
import type { PreconSelectedRecord } from "./precon-types";

const key = (projectId: string) => `otus:precon-selection:${projectId}`;

/**
 * Persist the slim selection for the builder to read on mount. Returns `true`
 * on success and `false` if the write threw (Safari private mode, disabled
 * storage, quota). Callers must check the result: navigating after a failed
 * write lands on a builder with no selection, which silently bounces back.
 */
export function writePreconSelection(
  projectId: string,
  records: PreconSelectedRecord[],
): boolean {
  try {
    sessionStorage.setItem(key(projectId), JSON.stringify(records));
    return true;
  } catch {
    // sessionStorage unavailable (private mode/quota) — caller surfaces an error.
    return false;
  }
}

export function readPreconSelection(
  projectId: string,
): PreconSelectedRecord[] | null {
  try {
    const raw = sessionStorage.getItem(key(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) && parsed.length > 0
      ? (parsed as PreconSelectedRecord[])
      : null;
  } catch {
    return null;
  }
}

export function clearPreconSelection(projectId: string): void {
  try {
    sessionStorage.removeItem(key(projectId));
  } catch {
    /* no-op */
  }
}

/**
 * Patch one record's plan/pin fields inside an in-flight hand-off projection,
 * so a pin placed, moved or removed after hand-off shows up when the builder
 * re-reads the selection (US-003 — see [[precon-report-builder.spec]]).
 * No-op when no selection exists for the project or the record isn't in it;
 * storage errors are swallowed — the projection is a best-effort snapshot and
 * the pin mutation it rides on must never fail because of it.
 */
export function patchPreconSelectionPin(
  projectId: string,
  recordId: string,
  patch: { plan: string | null; pin_x: number | null; pin_y: number | null },
): void {
  try {
    const raw = sessionStorage.getItem(key(projectId));
    if (!raw) return;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return;
    const records = parsed as PreconSelectedRecord[];
    const idx = records.findIndex((r) => r?.id === recordId);
    if (idx === -1) return;
    records[idx] = { ...records[idx], ...patch };
    sessionStorage.setItem(key(projectId), JSON.stringify(records));
  } catch {
    /* no-op */
  }
}
